// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import Router, { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import Button from '@/components/button';
import SelectInput from '@/components/select-input';
import TextInput from '@/components/text-input';
import Breadcrumbs from '@/components/breadcrumbs';
import { colors } from '@/theme';
import Fetch from '@/utils/Fetch';
import SettingsMenu from '@/components/settings/menu';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import ToastContext from '@/utils/toast-context';
import LookupSubMenu from '@/components/lookup/sub-menu';
import ConfirmAction from '@/components/modals/confirm-action';
import Storage from '@/utils/storage';
import constants from '@/utils/constants';
import { createFileRoute } from '@tanstack/react-router'

function CreateLookup() {
  const router = useRouter();
  const { type } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch

  const getTitleForType = (t: any) => {
    const titles = {
      customerType: 'Customer Type',
      customerStatus: 'Customer Status',
      industryType: 'Industry Type',
      mediaType: 'Media Type',
      queryType: 'Query Type',
      queryStatus: 'Query Status',
      queryReason: 'Query Reason',
      designation: 'Designation',
      faultCause: 'Fault Cause',
      faultCode: 'Fault Code',
      faultReason: 'Fault Reason',
    };
    return titles[t] || '';
  };
  const title = getTitleForType(type);

  const { data: queryTypesData } = useQuery(
    ['queryTypes'],
    () => Fetch.get({ url: '/QueryType' }),
    { enabled: type === 'queryStatus' }
  );
  const { data: inventoryCategoriesData } = useQuery(
    ['inventoryCategories'],
    () => Fetch.get({ url: '/InventoryCategory' }),
    { enabled: type === 'faultCode' }
  );
  const queryTypes = queryTypesData?.Results ?? null;
  const inventoryCategories = inventoryCategoriesData?.Results ?? null;

  if (!router.isReady) return null;

  const toast = useContext(ToastContext);

  let currPath = '';
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const [submitting, setSubmitting] = useState(false);
  const buttonText = `Create ${title}`;

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const getAccessStatus = () => {
    let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  }

  useEffect(() => {
    getAccessStatus();
  }, []);
  
  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace,"/");
    }
  }, [accessStatus]);

  Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, submitData);

  const [code, setCode] = useState('');
  const handleCodeChange = (e: any) => {
    setCode(e.target.value);
    setFormIsDirty(true);
  }

  const [description, setDescription] = useState('');
  const handleDescriptionChange = (e: any) => {
    setDescription(e.target.value);
    setFormIsDirty(true);
  }

  
  const [queryTypeSearch, setQueryTypeSearch] = useState('');
  const [selectedQueryType, setSelectedQueryType] = useState<any>();
  const handleQueryTypeChange = (e: any) => {
    setQueryTypeSearch(e.target.value);
    setFormIsDirty(true);
  }

  
  const [inventoryCategorySearch, setInventoryCategorySearch] = useState('');
  const [selectedInventoryCategory, setSelectedInventoryCategory] = useState<any>();
  const handleInventoryCategoryChange = (e: any) => {
    setInventoryCategorySearch(e.target.value);
    setFormIsDirty(true);
  }

  const [inputErrors, setInputErrors] = useState<any>({});

  const validate = () => {

    let validationItems = [];
    validationItems = [
      { key: 'Description', value: description, required: true, type: Enums.ControlType.Text },
    ];
    if (type == 'faultCause' || type == 'faultReason' || type == 'faultCode') {
      validationItems = [...validationItems,
      { key: 'Code', value: code, required: true, type: Enums.ControlType.Text },];
    }
    if (type == 'faultCode') {
      validationItems = [...validationItems,
      { key: 'InventoryCategory', value: selectedInventoryCategory, required: true, type: Enums.ControlType.Select },];
    }
    if (type == 'queryStatus') {
      validationItems = [...validationItems,
      { key: 'QueryType', value: selectedQueryType, required: true, type: Enums.ControlType.Select },];
    }

    const { isValid, errors } = Helper.validateInputs(validationItems);
    setInputErrors(errors);
    return isValid;
  }

  async function submitData() {
    setSubmitting(true);

    let isValid = validate();
    if (isValid) {
      let params = {};

      if (type == 'faultCause' || type == 'faultReason') {
        params = {
          Code: code, Description: description,
        };
      } else if (type == 'faultCode') {
        params = {
          Code: code, Description: description, InventoryCategoryID: selectedInventoryCategory.ID,
        };
      } else if (type == 'queryStatus') {
        params = {
          Description: description, QueryTypeID: selectedQueryType.ID,
        };
      } else {
        params = {
          Description: description
        };
      }

      const dataPost = await Fetch.post({
        url: `/${type}`,
        params: params
      });

      if (dataPost.ID) {
        Helper.mixpanelTrack(constants.mixPanelEvents.createOption, {
          "optionID": dataPost.ID,
          "type": type
        });
        setFormIsDirty(false);
        await Helper.waitABit();
        Helper.nextRouter(Router.push,`/settings/option/${dataPost.ID}?type=${type}`);
      }
    } else {
      toast.setToast!!!({
        message: 'There are errors on the page',
        show: true,
        type: 'error'
      });
      setSubmitting(false);
    }
    
    return isValid;
  }

  const setType = (t: any) => {
    Helper.nextRouter(Router.push,`/settings/option/list?type=${t}`);
  }

  return (
    <div className="settings-container">
      <div className="row">
        <SettingsMenu urlPath={currPath} />
        <LookupSubMenu type={type} setType={setType} />
        <div className="settings-card">
          <div className="section">
            <div className="row">
              <h3>{title} Details</h3>
            </div>
            {type == 'queryStatus' ?
              <div className="row">
                <div className="column">
                  <SelectInput
                    changeHandler={handleQueryTypeChange}
                    label="Query Type"
                    options={queryTypes}
                    placeholder="Select query type"
                    required={true}
                    noInput={true}
                    setSelected={setSelectedQueryType}
                    type="status"
                    value={queryTypeSearch}
                    error={inputErrors.QueryType}
                  />
                </div>
              </div>
              : ''
            }
            {type == 'faultCode' ?
              <div className="row">
                <div className="column">
                  <SelectInput
                    changeHandler={handleInventoryCategoryChange}
                    label="Inventory Category"
                    options={inventoryCategories}
                    placeholder="Apply to all"
                    required={true}
                    noInput={true}
                    setSelected={setSelectedInventoryCategory}
                    type="status"
                    value={inventoryCategorySearch}
                    error={inputErrors.InventoryCategory}
                  />
                </div>
              </div>
              : ''
            }
            {type == 'faultCause' || type == 'faultCode' || type == 'faultReason' ?
              <div className="row">
                <div className="column">
                  <TextInput
                    label="Code"
                    changeHandler={handleCodeChange}
                    required={true}
                    value={code}
                    error={inputErrors.Code}
                  />
                </div>
              </div>
              : ''
            }
            <div className="row">
              <div className="column">
                <TextInput
                  label="Description"
                  changeHandler={handleDescriptionChange}
                  required={true}
                  value={description}
                  error={inputErrors.Description}
                />
              </div>
            </div>
            <div className="row">
              <Button text={buttonText} onClick={submitData} disabled={submitting} />
            </div>
          </div>
        </div>
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

      <style jsx>{`
        .row {
          display: flex;
        }
        .column {
          display: flex;
          flex-basis: 0;
          flex-direction: column;
          flex-grow: 1;
        }
        .column :global(.textarea-container) {
          height: 100%;
        }
        .column + .column {
          margin-left: 1.25rem;
        }
      `}</style>
    </div>
  );
}

export default CreateLookup

export const Route = createFileRoute('/_authenticated/settings/option/create')({ component: CreateLookup })
